import React, { useMemo, useState } from 'react';
import { ArrowLeftIcon, PrinterIcon } from '@heroicons/react/24/outline';
import { Link } from 'react-router-dom';
import {
  useListStudentMedicationsQuery,
  useLocalGetStudentsQuery,
  useLocalGetLocationByIdQuery,
} from '../../store/api/localApi';
import {
  MedicationAdministrationEntry,
  MedicationEntryType,
  MedicationRequirementType,
  MedicationRequirementWithEntries,
  Student,
} from '../../types';
import { studentDisplayName } from '../../utils/student';
import {
  DAY_LABELS,
  ENTRY_TYPE_LABELS,
  ROUTE_LABELS,
  STATUS_LABELS,
  formatDate,
  formatDateTime,
  isoDaysAgo,
  toIsoDate,
} from './medication-labels';
import './medication-print.css';

/** The default window: the last 90 days, adjustable by the two date inputs. */
const DEFAULT_RANGE_DAYS = 90;

interface Props {
  studentId: string;
}

/**
 * Whether a requirement belongs in a record covering [from, to].
 *
 * TWO independent reasons to include it, and the OR is the point:
 *
 *   1. It has at least one administration entry in the range. Obvious.
 *   2. It was ACTIVE at any point during the range — i.e. it started on or
 *      before `to` and had not ended before `from`.
 *
 * Reason 2 is what makes a standing requirement started two years ago print
 * with its recent doses. A record that showed only requirements *created* in
 * the window would omit exactly the long-running medications an inspector most
 * wants to see, and would do it silently.
 */
function isInRange(
  requirement: MedicationRequirementWithEntries,
  entriesInRange: MedicationAdministrationEntry[],
  from: string,
  to: string
): boolean {
  if (entriesInRange.length > 0) return true;
  const startedByEnd = requirement.startDate <= to;
  const notEndedBeforeStart = !requirement.endDate || requirement.endDate >= from;
  return startedByEnd && notEndedBeforeStart;
}

/**
 * The print-optimised medication record for one child — the document an
 * inspector reads.
 *
 * BROWSER PRINT-TO-PDF, not server-side generation. `window.print()` plus
 * `medication-print.css`, exactly as the incident inspector output does it
 * (IncidentDetail.print.css). A server-rendered PDF would mean a new
 * dependency, a rendering Lambda, an S3 artifact path and a presigned
 * download — a distribution decision, and not one this feature gets to make.
 *
 * The range filters ENTRIES by `administeredAt`; requirements are selected by
 * `isInRange` above. An empty range prints the header and an explicit notice —
 * never a blank page, because a blank page is indistinguishable from a broken
 * one, and an inspector cannot tell "no doses" from "the system failed".
 *
 * Two facts are flagged rather than left to be read out of the notes, on paper
 * as well as on screen: a dose given without a second witness, and an
 * emergency dose given past the medication's expiry date. The spec requires
 * both in all three surfaces (the child's log, this record, and the location
 * register) — anything less is a gap an inspector would find.
 */
const MedicationPrintView: React.FC<Props> = ({ studentId }) => {
  const [from, setFrom] = useState(() => isoDaysAgo(DEFAULT_RANGE_DAYS));
  const [to, setTo] = useState(() => toIsoDate(new Date()));

  const { data, isLoading, isError } = useListStudentMedicationsQuery({ studentId });

  // The child and the service are header facts, so both are resolved from the
  // record itself rather than the viewer's active context: an org admin
  // browsing across locations must get the child's REAL service on a printout,
  // not wherever they happen to be scoped. Every requirement for a child
  // carries the same locationId, so the first one is authoritative.
  const recordLocationId = data?.requirements[0]?.locationId;
  const { data: students } = useLocalGetStudentsQuery(recordLocationId ?? '', {
    skip: !recordLocationId,
  });
  const { data: location } = useLocalGetLocationByIdQuery(recordLocationId ?? '', {
    skip: !recordLocationId,
  });
  const child = students?.find((s: Student) => s.id === studentId);

  /**
   * Requirements in range, each carrying only its in-range entries, newest
   * first, with corrections attached to the entry they correct.
   */
  const rows = useMemo(() => {
    const requirements = data?.requirements ?? [];

    return requirements
      .map((requirement) => {
        // Entries are filtered on the Dublin CALENDAR DAY of administeredAt, so
        // a range of [x, x] means the whole of that day rather than the single
        // instant of its midnight.
        const inRange = requirement.entries.filter((e) => {
          const day = toIsoDate(new Date(e.administeredAt));
          return day >= from && day <= to;
        });

        // A CORRECTION amends an earlier entry rather than standing alone;
        // showing it as a peer row would read as an extra dose. It renders
        // beneath its target, and the original is never removed — that is the
        // whole point of an append-only log.
        const corrections = new Map<string, MedicationAdministrationEntry[]>();
        for (const entry of inRange) {
          if (entry.entryType !== MedicationEntryType.CORRECTION || !entry.correctsEntryId)
            continue;
          const bucket = corrections.get(entry.correctsEntryId);
          if (bucket) bucket.push(entry);
          else corrections.set(entry.correctsEntryId, [entry]);
        }

        // A correction whose target fell outside the range would otherwise
        // vanish entirely, so it stays in the main list rather than being
        // orphaned onto an entry that is not on the page.
        const targetIds = new Set(inRange.map((e) => e.entryId));
        const primary = inRange
          .filter(
            (e) =>
              e.entryType !== MedicationEntryType.CORRECTION ||
              !e.correctsEntryId ||
              !targetIds.has(e.correctsEntryId)
          )
          .sort((a, b) => b.administeredAt.localeCompare(a.administeredAt));

        return { requirement, entries: inRange, primary, corrections };
      })
      .filter((row) => isInRange(row.requirement, row.entries, from, to));
  }, [data, from, to]);

  const totalEntries = rows.reduce((n, row) => n + row.entries.length, 0);

  if (isLoading) {
    return (
      <div
        className="min-h-[40vh] flex items-center justify-center text-sm text-gray-500"
        data-testid="medication-print-loading"
      >
        Loading medication record…
      </div>
    );
  }

  if (isError) {
    return (
      <div
        className="min-h-[40vh] flex items-center justify-center text-sm text-gray-500"
        data-testid="medication-print-error"
      >
        Failed to load the medication record.
      </div>
    );
  }

  const childName = studentDisplayName(child);
  const serviceName = location?.name ?? 'This service';

  return (
    <div data-testid="medication-print-view" className="max-w-4xl mx-auto px-4 py-8">
      {/* Screen-only control bar. Hidden on paper (medication-print-only-hide):
          a date picker and a "Print" button are meaningless once printed. */}
      <div className="medication-print-only-hide space-y-4">
        <div className="flex items-center justify-between gap-3">
          <Link
            to={`/students/${studentId}`}
            data-testid="medication-print-back"
            className="inline-flex items-center gap-1 text-sm text-gray-500 hover:text-gray-700"
          >
            <ArrowLeftIcon className="h-4 w-4" aria-hidden="true" />
            Back to child
          </Link>
          <button
            type="button"
            data-testid="medication-print-button"
            onClick={() => window.print()}
            className="inline-flex items-center gap-1.5 px-4 py-2 text-sm rounded-md bg-gray-800 text-white hover:bg-gray-900"
          >
            <PrinterIcon className="h-4 w-4" aria-hidden="true" />
            Print
          </button>
        </div>

        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4 flex flex-wrap gap-3 items-end">
          <div>
            <label
              htmlFor="medication-print-range-start"
              className="block text-xs font-medium text-gray-500 mb-1"
            >
              From
            </label>
            <input
              id="medication-print-range-start"
              type="date"
              data-testid="medication-print-range-start"
              value={from}
              onChange={(e) => setFrom(e.target.value)}
              className="rounded-md border-gray-300 text-sm"
            />
          </div>
          <div>
            <label
              htmlFor="medication-print-range-end"
              className="block text-xs font-medium text-gray-500 mb-1"
            >
              To
            </label>
            <input
              id="medication-print-range-end"
              type="date"
              data-testid="medication-print-range-end"
              value={to}
              onChange={(e) => setTo(e.target.value)}
              className="rounded-md border-gray-300 text-sm"
            />
          </div>
          <p className="text-xs text-gray-500 pb-2">
            Defaults to the last {DEFAULT_RANGE_DAYS} days.
          </p>
        </div>
      </div>

      {/* The record header. Rendered on screen AND on paper — the child, the
          service and the range are what identify the document, and a printout
          without them is unattributable. */}
      <header
        data-testid="medication-print-header"
        className="mt-4 border-b-2 border-gray-800 pb-3"
      >
        <h1 className="text-xl font-bold text-gray-900">Medication record</h1>
        <dl className="mt-1 text-sm text-gray-700 space-y-0.5">
          <div>
            <dt className="inline font-medium">Child: </dt>
            <dd className="inline" data-testid="medication-print-child-name">
              {childName}
            </dd>
          </div>
          <div>
            <dt className="inline font-medium">Service: </dt>
            <dd className="inline" data-testid="medication-print-service-name">
              {serviceName}
            </dd>
          </div>
          <div>
            <dt className="inline font-medium">Period: </dt>
            <dd className="inline" data-testid="medication-print-range-label">
              {formatDate(from)} to {formatDate(to)}
            </dd>
          </div>
          <div>
            <dt className="inline font-medium">Printed: </dt>
            <dd className="inline">{formatDateTime(new Date().toISOString())}</dd>
          </div>
        </dl>
      </header>

      {/* An empty range prints the header above and this notice — never a
          blank page. A blank page cannot be told apart from a broken one. */}
      {totalEntries === 0 && (
        <p
          data-testid="medication-print-empty-notice"
          className="mt-6 border border-gray-300 px-4 py-3 text-sm text-gray-800"
        >
          No doses recorded in this period.
          {rows.length > 0
            ? ` ${rows.length} medication requirement${
                rows.length === 1 ? ' was' : 's were'
              } in place during it, listed below.`
            : ' No medication requirement was in place during it either.'}
        </p>
      )}

      <div className="mt-6 space-y-5">
        {rows.map(({ requirement, primary, corrections }) => (
          <PrintRequirement
            key={requirement.requirementId}
            requirement={requirement}
            primary={primary}
            corrections={corrections}
          />
        ))}
      </div>
    </div>
  );
};

const PrintRequirement: React.FC<{
  requirement: MedicationRequirementWithEntries;
  primary: MedicationAdministrationEntry[];
  corrections: Map<string, MedicationAdministrationEntry[]>;
}> = ({ requirement, primary, corrections }) => {
  const { consent } = requirement;

  return (
    <section
      data-testid={`medication-print-requirement-${requirement.requirementId}`}
      className="medication-print-requirement bg-white border border-gray-300 p-4"
    >
      <div className="flex items-baseline justify-between gap-3 flex-wrap">
        <h2 className="text-base font-semibold text-gray-900">
          {requirement.medicationName} — {requirement.dosage}
        </h2>
        <span
          data-testid={`medication-print-status-${requirement.requirementId}`}
          className="text-xs font-semibold text-gray-700"
        >
          {STATUS_LABELS[requirement.status]}
        </span>
      </div>

      <dl className="mt-2 grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-0.5 text-xs text-gray-700">
        <div>
          <dt className="inline font-medium">Route: </dt>
          <dd className="inline">{ROUTE_LABELS[requirement.route]}</dd>
        </div>
        <div>
          <dt className="inline font-medium">Schedule: </dt>
          <dd className="inline">
            {requirement.requirementType === MedicationRequirementType.EMERGENCY_ONLY
              ? 'Emergency use only — not scheduled'
              : requirement.schedule
              ? `${requirement.schedule.timesOfDay.join(', ')} on ${requirement.schedule.daysOfWeek
                  .map((d) => DAY_LABELS[d] ?? d)
                  .join(', ')}`
              : 'No schedule recorded'}
          </dd>
        </div>
        <div>
          <dt className="inline font-medium">Prescriber: </dt>
          <dd className="inline">{requirement.prescriber || 'Not recorded'}</dd>
        </div>
        <div>
          <dt className="inline font-medium">Period: </dt>
          <dd className="inline">
            {formatDate(requirement.startDate)}
            {requirement.endDate ? ` to ${formatDate(requirement.endDate)}` : ' onwards'}
          </dd>
        </div>
        {requirement.medicationExpiryDate && (
          <div>
            <dt className="inline font-medium">Medication expires: </dt>
            <dd className="inline">{formatDate(requirement.medicationExpiryDate)}</dd>
          </div>
        )}
        {/* CONSENT STATE. The one fact that authorises staff to give the
            medication at all, so it is stated explicitly — who confirmed it
            and when — rather than implied by the status. A withdrawal is
            shown ALONGSIDE the confirmation, never in place of it: the record
            must be able to answer "consented on X, withdrawn on Y". */}
        <div
          className="sm:col-span-2"
          data-testid={`medication-print-consent-${requirement.requirementId}`}
        >
          <dt className="inline font-medium">Consent: </dt>
          <dd className="inline">
            {/* Prefer the denormalised name: an inspector asking "who
                authorised this?" cannot read a uuid. Rows consented before
                confirmedByName existed fall back to the id rather than
                rendering nothing. */}
            {consent?.confirmedAt
              ? `Confirmed ${formatDateTime(consent.confirmedAt)}${
                  consent.confirmedByName
                    ? ` by ${consent.confirmedByName}`
                    : consent.confirmedByUserId
                    ? ` by guardian ${consent.confirmedByUserId}`
                    : ''
                }`
              : 'Not confirmed'}
            {consent?.revokedAt
              ? ` · Withdrawn ${formatDateTime(consent.revokedAt)}${
                  consent.revokedReason ? ` — ${consent.revokedReason}` : ''
                }`
              : ''}
          </dd>
        </div>
      </dl>

      {requirement.requirementType === MedicationRequirementType.EMERGENCY_ONLY &&
        requirement.emergencyInstructions && (
          <p className="mt-2 text-xs text-gray-800">
            <span className="font-medium">Emergency instructions: </span>
            {requirement.emergencyInstructions}
          </p>
        )}

      {requirement.specialInstructions && (
        <p className="mt-1 text-xs text-gray-800">
          <span className="font-medium">Special instructions: </span>
          {requirement.specialInstructions}
        </p>
      )}

      <h3 className="mt-3 text-xs font-semibold uppercase tracking-wide text-gray-500">
        Administration log
      </h3>
      {primary.length === 0 ? (
        <p
          className="mt-1 text-xs text-gray-600"
          data-testid={`medication-print-no-entries-${requirement.requirementId}`}
        >
          No doses recorded in this period.
        </p>
      ) : (
        <ul className="mt-1 divide-y divide-gray-200">
          {primary.map((entry) => (
            <li
              key={entry.entryId}
              data-testid={`medication-print-entry-${entry.entryId}`}
              className="medication-print-entry py-2"
            >
              <PrintEntryBody entry={entry} />
              {(corrections.get(entry.entryId) ?? []).map((correction) => (
                <div
                  key={correction.entryId}
                  data-testid={`print-correction-${correction.entryId}`}
                  className="medication-print-correction mt-1.5 ml-3 pl-2 border-l-2 border-gray-400"
                >
                  <p className="text-[11px] font-semibold text-gray-700">
                    Correction to the entry above — the original stays on the record.
                  </p>
                  <PrintEntryBody entry={correction} />
                </div>
              ))}
            </li>
          ))}
        </ul>
      )}
    </section>
  );
};

/** One log row: what, when, how much, who gave it, who witnessed it. */
const PrintEntryBody: React.FC<{ entry: MedicationAdministrationEntry }> = ({ entry }) => (
  <>
    <div className="flex flex-wrap items-baseline gap-x-3 text-xs text-gray-900">
      <span className="font-semibold">{ENTRY_TYPE_LABELS[entry.entryType]}</span>
      <span>{formatDateTime(entry.administeredAt)}</span>
      {entry.doseGiven && <span>Dose: {entry.doseGiven}</span>}
      {entry.scheduledSlot && (
        <span className="text-gray-500">Scheduled {entry.scheduledSlot}</span>
      )}
    </div>

    {/* Both signatures on one line. The witness is a named person or an
        explicit "none" — a blank would read as an oversight in transcription
        rather than the recorded fact that nobody witnessed the dose. */}
    <div className="mt-0.5 text-xs text-gray-700">
      Administered by {entry.administeredByName}
      {entry.witnessStaffName ? ` · Witnessed by ${entry.witnessStaffName}` : ' · No witness'}
    </div>

    {entry.unwitnessedReason && (
      <p className="mt-1 text-xs text-amber-900">
        <span
          data-testid="print-unwitnessed-flag"
          className="medication-print-flag inline-block px-1.5 py-0.5 rounded bg-amber-100 font-semibold"
        >
          UNWITNESSED
        </span>{' '}
        Given without a second staff witness — {entry.unwitnessedReason}
      </p>
    )}

    {entry.administeredPastExpiry && (
      <p className="mt-1 text-xs text-red-900">
        <span
          data-testid="print-past-expiry-flag"
          className="medication-print-flag inline-block px-1.5 py-0.5 rounded bg-red-100 font-semibold"
        >
          PAST EXPIRY
        </span>{' '}
        The medication was past its expiry date when this dose was given.
      </p>
    )}

    {entry.overrideReason && (
      <p className="mt-1 text-xs text-gray-700">
        <span className="font-medium">Override reason: </span>
        {entry.overrideReason}
      </p>
    )}

    {entry.notes && <p className="mt-1 text-xs text-gray-700">{entry.notes}</p>}
  </>
);

export default MedicationPrintView;
